import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { 
  HandThumbUpIcon as ThumbUpOutline,
  StarIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as ThumbUpSolid } from '@heroicons/react/24/solid';

import { API_BASE } from '../lib/config';
const API = `${API_BASE}`;

const EmployerRatingSection = ({ profileId }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ratingStats, setRatingStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGoodModal, setShowGoodModal] = useState(false);
  const [goodComment, setGoodComment] = useState('');
  const [submittingGood, setSubmittingGood] = useState(false);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);

  useEffect(() => {
    fetchRatingStats();
    fetchReviews();
  }, [profileId]);

  const fetchRatingStats = async () => {
    try {
      const response = await fetch(`${API}/employer-profiles/${profileId}/rating-stats`);
      if (response.ok) {
        const data = await response.json();
        setRatingStats(data);
      }
    } catch (error) {
      console.error('Error fetching rating stats:', error);
    }
  };

  const fetchReviews = async (skip = 0, limit = 5) => {
    try {
      const response = await fetch(`${API}/employer-profiles/${profileId}/reviews?skip=${skip}&limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setHasMoreReviews(data.has_more || false);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoodClick = () => {
    if (!user) {
      toast.error('ログインが必要です');
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      toast.error('求職者のみが評価を投稿できます');
      return;
    }

    setShowGoodModal(true);
  };

  const submitGoodReview = async () => {
    if (!user) {
      toast.error('ログインが必要です');
      return;
    }

    try {
      setSubmittingGood(true);
      const response = await fetch(`${API}/employer-profiles/${profileId}/good-reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employer_profile_id: profileId,
          comment: goodComment
        })
      });

      if (response.ok) {
        toast.success('グッド評価を投稿しました！');
        setShowGoodModal(false);
        setGoodComment('');
        // データを再取得
        fetchRatingStats();
        fetchReviews();
      } else {
        const error = await response.json();
        toast.error(error.detail || '評価の投稿に失敗しました');
      }
    } catch (error) {
      console.error('Error submitting good review:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setSubmittingGood(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-36"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
      {/* 店舗の評価ヘッダー */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">店舗の評価</h2>
        
        {/* Good率の表示 */}
        {ratingStats && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex items-center mr-4">
                <ThumbUpSolid className="h-8 w-8 text-yellow-400 mr-2" />
                <span className="text-3xl font-bold text-gray-900">
                  {ratingStats.good_percentage}%
                </span>
              </div>
              <div className="text-sm text-gray-500">
                <div className="flex items-center">
                  <span className="mr-1"></span>
                  <span>この数字について</span>
                </div>
              </div>
            </div>
            
            {/* グッドボタン */}
            {user && user.user_type === 'job_seeker' && (
              <button
                onClick={handleGoodClick}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                <ThumbUpOutline className="h-4 w-4 mr-2" />
                グッド
              </button>
            )}
          </div>
        )}
      </div>

      {/* レビューセクション */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-3">レビュー</h3>
        
        {reviews.length === 0 ? (
          <p className="text-gray-500 text-sm">まだレビューがありません</p>
        ) : (
          <div className="space-y-4">
            {reviews.map((review, index) => (
              <div key={review.id || index} className="border-b border-gray-100 pb-4 last:border-b-0">
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="font-medium text-sm text-gray-900">
                        {review.reviewer_age_group} {review.reviewer_gender}
                      </span>
                      <span className="mx-2 text-gray-300"></span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-gray-700 mb-2">{review.comment}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      {formatDate(review.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* もっと見るボタン */}
            {hasMoreReviews && (
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => navigate(`/employer-profile/${profileId}/reviews`)}
                  className="flex items-center justify-between w-full p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <span className="font-medium">もっと見る</span>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-2">
                      {ratingStats ? `${ratingStats.total_reviews}件` : ''}
                    </span>
                    <ChevronRightIcon className="h-4 w-4" />
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* グッド評価モーダル */}
      {showGoodModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              グッド評価を投稿
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              この事業者での仕事体験はいかがでしたか？
            </p>
            
            <textarea
              value={goodComment}
              onChange={(e) => setGoodComment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              rows={4}
              placeholder="働いてみた感想をお聞かせください（任意）"
            />
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowGoodModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={submittingGood}
              >
                キャンセル
              </button>
              <button
                onClick={submitGoodReview}
                disabled={submittingGood}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submittingGood ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    投稿中...
                  </>
                ) : (
                  <>
                    <ThumbUpOutline className="h-4 w-4 mr-2" />
                    グッド評価を投稿
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerRatingSection;